import React, { useRef, useState, useEffect, useCallback } from 'react';
import Webcam from 'react-webcam';
import { X, Camera, AlertCircle, Loader2, Hand, RefreshCw } from 'lucide-react';
import * as handpose from '@tensorflow-models/handpose';
import '@tensorflow/tfjs';

interface WebcamPalmReaderProps {
  onCapture: (imageData: string) => void;
  onCancel: () => void;
}

const WebcamPalmReader: React.FC<WebcamPalmReaderProps> = ({ onCapture, onCancel }) => {
  const webcamRef = useRef<Webcam>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isModelLoading, setIsModelLoading] = useState(true);
  const [model, setModel] = useState<handpose.HandPose | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isHandDetected, setIsHandDetected] = useState(false);
  const [isHandAligned, setIsHandAligned] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [requestingPermission, setRequestingPermission] = useState(false);
  const requestAnimationRef = useRef<number>();
  const alignmentTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Load the handpose model
  useEffect(() => {
    const loadModel = async () => {
      try {
        setIsModelLoading(true);
        const loadedModel = await handpose.load();
        setModel(loadedModel);
        setIsModelLoading(false);
        console.log('Handpose model loaded');
      } catch (err) {
        console.error('Error loading handpose model:', err);
        setError('Failed to load hand detection model. You can still use manual capture.');
        setIsModelLoading(false);
      }
    };

    loadModel();

    return () => {
      if (requestAnimationRef.current) {
        cancelAnimationFrame(requestAnimationRef.current);
      }
      if (alignmentTimerRef.current) {
        clearTimeout(alignmentTimerRef.current);
      }
    };
  }, []);

  // Request camera permission explicitly
  const requestCameraPermission = async () => {
    setRequestingPermission(true);
    setError(null);
    
    try {
      // Request camera access explicitly using getUserMedia
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 }
        }
      });
      
      // Stop the stream immediately - we just needed to get permission
      stream.getTracks().forEach(track => track.stop());
      
      // Permission granted, now we can show the Webcam component
      setPermissionGranted(true);
      setRequestingPermission(false);
    } catch (err: any) {
      console.error('Camera permission error:', err);
      setRequestingPermission(false);
      
      let errorMessage = 'Unable to access camera. ';

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Camera access was blocked.\n\nTo enable it:\n\nOn Android Chrome:\n1. Tap the lock icon (🔒) or info icon (ⓘ) in the address bar\n2. Tap "Permissions" or "Site settings"\n3. Find "Camera" and set it to "Allow"\n4. Reload this page\n\nOn iPhone Safari:\n1. Go to iPhone Settings > Safari > Camera\n2. Select "Ask" or "Allow"\n3. Return here and reload\n\nAlternatively, you can use the "Upload Photo" option below instead.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'No camera found on your device.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'Camera is already in use by another app. Please close other apps and try again.';
      } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        errorMessage = 'Camera does not support the requested settings. Try switching cameras.';
      } else if (err.name === 'SecurityError') {
        errorMessage = 'Camera access blocked by browser security.\n\nTo enable it:\n\nOn Android Chrome:\n1. Tap the lock icon (🔒) or info icon (ⓘ) in the address bar\n2. Tap "Permissions" or "Site settings"\n3. Find "Camera" and set it to "Allow"\n4. Reload this page\n\nAlternatively, you can use the "Upload Photo" option below instead.';
      } else {
        errorMessage = `Unable to access camera: ${err.message || 'Unknown error'}`;
      }

      setError(errorMessage);
    }
  };

  // Update dimensions on window resize
  useEffect(() => {
    const handleResize = () => {
      if (webcamRef.current && webcamRef.current.video) {
        const video = webcamRef.current.video;
        setDimensions({
          width: video.videoWidth,
          height: video.videoHeight
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle webcam ready state
  const handleWebcamReady = () => {
    setIsCameraReady(true);
    setError(null);
    if (webcamRef.current && webcamRef.current.video) {
      const video = webcamRef.current.video;
      setDimensions({
        width: video.videoWidth,
        height: video.videoHeight
      });
    }
  };

  // Detect hands in the webcam feed
  const detectHands = useCallback(async () => {
    if (
      model &&
      webcamRef.current &&
      webcamRef.current.video &&
      webcamRef.current.video.readyState === 4 &&
      canvasRef.current
    ) {
      const video = webcamRef.current.video;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      try {
        // Detect hands
        const predictions = await model.estimateHands(video);

        if (predictions.length > 0) {
          setIsHandDetected(true);

          // Draw hand landmarks
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Draw palm outline
            const palmPoints = predictions[0].landmarks.filter((_, i) =>
              [0, 1, 2, 5, 9, 13, 17].includes(i)
            );

            if (palmPoints.length >= 7) {
              ctx.beginPath();
              ctx.moveTo(palmPoints[0][0], palmPoints[0][1]);
              for (let i = 1; i < palmPoints.length; i++) {
                ctx.lineTo(palmPoints[i][0], palmPoints[i][1]);
              }
              ctx.closePath();
              ctx.strokeStyle = 'rgba(0, 255, 0, 0.7)';
              ctx.lineWidth = 3;
              ctx.stroke();

              // Check if hand is aligned with center
              const centerX = canvas.width / 2;
              const centerY = canvas.height / 2;

              // Calculate palm center
              const palmCenter = palmPoints.reduce(
                (acc, point) => [acc[0] + point[0], acc[1] + point[1]],
                [0, 0]
              );
              palmCenter[0] /= palmPoints.length;
              palmCenter[1] /= palmPoints.length;

              // Calculate distance from center
              const distance = Math.sqrt(
                Math.pow(palmCenter[0] - centerX, 2) +
                Math.pow(palmCenter[1] - centerY, 2)
              );

              // Check if palm is centered and open
              const isAligned = distance < canvas.width * 0.15;
              setIsHandAligned(isAligned);

              // Draw alignment indicator
              ctx.beginPath();
              ctx.arc(centerX, centerY, canvas.width * 0.15, 0, 2 * Math.PI);
              ctx.strokeStyle = isAligned ? 'rgba(0, 255, 0, 0.5)' : 'rgba(255, 0, 0, 0.5)';
              ctx.lineWidth = 2;
              ctx.stroke();

              // Start countdown if hand is aligned
              if (isAligned) {
                if (!alignmentTimerRef.current) {
                  alignmentTimerRef.current = setTimeout(() => {
                    captureImage();
                    if (alignmentTimerRef.current) {
                      clearTimeout(alignmentTimerRef.current);
                      alignmentTimerRef.current = null;
                    }
                  }, 2000);

                  // Start countdown
                  setCountdown(2);
                  const countdownInterval = setInterval(() => {
                    setCountdown((prev) => {
                      if (prev === null || prev <= 1) {
                        clearInterval(countdownInterval);
                        return null;
                      }
                      return prev - 1;
                    });
                  }, 1000);
                }
              } else {
                // Reset countdown if hand is no longer aligned
                if (alignmentTimerRef.current) {
                  clearTimeout(alignmentTimerRef.current);
                  alignmentTimerRef.current = null;
                  setCountdown(null);
                }
              }
            }
          }
        } else {
          setIsHandDetected(false);
          setIsHandAligned(false);

          // Reset countdown if no hand is detected
          if (alignmentTimerRef.current) {
            clearTimeout(alignmentTimerRef.current);
            alignmentTimerRef.current = null;
            setCountdown(null);
          }

          // Clear canvas
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Draw target area
            ctx.beginPath();
            ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width * 0.15, 0, 2 * Math.PI);
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        }
      } catch (err) {
        console.error('Error detecting hands:', err);
      }

      // Continue detection loop
      requestAnimationRef.current = requestAnimationFrame(detectHands);
    } else {
      // If not ready yet, try again in the next frame
      requestAnimationRef.current = requestAnimationFrame(detectHands);
    }
  }, [model]);

  // Start hand detection when model and camera are ready
  useEffect(() => {
    if (model && isCameraReady && !isModelLoading) {
      detectHands();
    }
  }, [model, isCameraReady, isModelLoading, detectHands]);

  // Capture the image when hand is aligned
  const captureImage = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        onCapture(imageSrc);
      }
    }
  }, [onCapture]);

  // Toggle camera between front and back
  const toggleCamera = () => {
    setIsCameraReady(false);
    setPermissionGranted(false);
    setFacingMode(prevMode => prevMode === 'user' ? 'environment' : 'user');
    // Re-request permission with new facing mode
    setTimeout(() => requestCameraPermission(), 100);
  };

  // Retry camera access
  const retryCamera = () => {
    setError(null);
    setIsCameraReady(false);
    setPermissionGranted(false);

    // Check if we can query permission status
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'camera' as PermissionName })
        .then(permissionStatus => {
          if (permissionStatus.state === 'granted') {
            // Permission already granted, just retry
            requestCameraPermission();
          } else if (permissionStatus.state === 'denied') {
            // Permission explicitly denied, show manual instructions
            setError('Camera access was blocked.\n\nTo enable it:\n\nOn Android Chrome:\n1. Tap the lock icon (🔒) or info icon (ⓘ) in the address bar\n2. Tap "Permissions" or "Site settings"\n3. Find "Camera" and set it to "Allow"\n4. Reload this page\n\nOn iPhone Safari:\n1. Go to iPhone Settings > Safari > Camera\n2. Select "Ask" or "Allow"\n3. Return here and reload\n\nAlternatively, you can use the "Upload Photo" option below instead.');
          } else {
            // Permission not determined yet, request it
            requestCameraPermission();
          }
        })
        .catch(() => {
          // Permissions API not supported, just try to request
          requestCameraPermission();
        });
    } else {
      // Permissions API not available, just try to request
      requestCameraPermission();
    }
  };

  return (
    <div className="relative flex flex-col items-center">
      <div className="relative w-full max-w-md">
        {/* Camera feed */}
        <div className="relative rounded-lg overflow-hidden bg-black" style={{ minHeight: '400px' }}>
          {/* Show permission request button if not granted yet */}
          {!permissionGranted && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-purple-900 to-indigo-900 text-white p-6">
              <Camera className="w-20 h-20 mb-6 text-purple-300" />
              <h3 className="text-2xl font-semibold mb-3 text-center">Camera Access Required</h3>
              <p className="text-center text-gray-200 mb-6 max-w-sm">
                We need access to your camera to capture your palm for reading.
              </p>
              <button
                onClick={requestCameraPermission}
                disabled={requestingPermission}
                className="px-8 py-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-lg flex items-center gap-3 disabled:opacity-50"
              >
                {requestingPermission ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    Requesting Access...
                  </>
                ) : (
                  <>
                    <Camera className="w-6 h-6" />
                    Allow Camera Access
                  </>
                )}
              </button>
              <p className="text-sm text-gray-300 mt-4 text-center max-w-sm">
                Your browser will ask for permission. Please tap "Allow" to continue.
              </p>
            </div>
          )}

          {/* Webcam component - only render after permission is granted */}
          {permissionGranted && !error && (
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                facingMode: facingMode,
                width: { ideal: 1280, min: 640 },
                height: { ideal: 720, min: 480 }
              }}
              onUserMedia={handleWebcamReady}
              onUserMediaError={(err: any) => {
                console.error('Camera error:', err);
                let errorMessage = 'Unable to access camera. ';

                if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                  errorMessage = 'Camera permission denied. Please check your browser settings and allow camera access.';
                } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                  errorMessage = 'No camera found on your device.';
                } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                  errorMessage = 'Camera is already in use by another app. Please close other apps and try again.';
                } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
                  errorMessage = 'Camera does not support the requested settings. Try switching cameras.';
                } else if (err.name === 'SecurityError') {
                  errorMessage = 'Camera access blocked. Please check your browser settings to allow camera access for this site.';
                } else {
                  errorMessage = 'Unable to access camera. Please ensure your browser has camera permissions.';
                }

                setError(errorMessage);
                setPermissionGranted(false);
              }}
              className="w-full h-auto"
              style={{ display: isCameraReady && !error ? 'block' : 'none' }}
              mirrored={facingMode === 'user'}
            />
          )}

          {/* Canvas overlay for hand tracking */}
          <canvas
            ref={canvasRef}
            className="absolute top-0 left-0 w-full h-full"
            style={{ display: isCameraReady && !error ? 'block' : 'none' }}
          />

          {/* Error message */}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-90 text-white p-6">
              <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-center">Camera Access Issue</h3>
              <p className="text-center text-gray-300 mb-6 max-w-sm whitespace-pre-line text-sm">{error}</p>
              <div className="flex gap-3 flex-wrap justify-center">
                {error.includes('blocked') || error.includes('denied') ? (
                  <button
                    onClick={() => window.location.reload()}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2"
                  >
                    <RefreshCw className="w-5 h-5" />
                    Reload Page
                  </button>
                ) : (
                  <button
                    onClick={retryCamera}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center gap-2"
                  >
                    <RefreshCw className="w-5 h-5" />
                    Try Again
                  </button>
                )}
                <button
                  onClick={onCancel}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  Go Back
                </button>
              </div>
            </div>
          )}

          {/* Loading indicator */}
          {permissionGranted && !error && (isModelLoading || !isCameraReady) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-70 text-white">
              <Loader2 className="w-12 h-12 animate-spin mb-4" />
              <p className="text-center px-4">
                {isModelLoading ? 'Loading hand detection...' : 'Starting camera...'}
              </p>
            </div>
          )}

          {/* Hand alignment guide */}
          {isCameraReady && !isModelLoading && !isHandDetected && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="w-1/2 h-1/2 border-2 border-dashed border-white rounded-full opacity-50 flex items-center justify-center">
                <Hand className="w-16 h-16 text-white opacity-70" />
              </div>
              <p className="text-white mt-4 bg-black bg-opacity-50 p-2 rounded">
                Show your palm to the camera
              </p>
            </div>
          )}

          {/* Countdown indicator */}
          {countdown !== null && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-black bg-opacity-50 text-white text-5xl font-bold rounded-full w-20 h-20 flex items-center justify-center">
                {countdown}
              </div>
            </div>
          )}

          {/* Status indicators */}
          {isCameraReady && !error && (
            <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center gap-2 flex-wrap">
              <div className={`px-3 py-1 rounded-full text-sm ${isHandDetected ? 'bg-green-500' : 'bg-red-500'} text-white`}>
                {isHandDetected ? 'Hand Detected' : 'No Hand'}
              </div>
              <div className={`px-3 py-1 rounded-full text-sm ${isHandAligned ? 'bg-green-500' : 'bg-yellow-500'} text-white`}>
                {isHandAligned ? 'Aligned' : 'Center Palm'}
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="mt-4 flex justify-between gap-2 flex-wrap">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
          >
            <X className="w-5 h-5" />
            Cancel
          </button>

          <button
            onClick={toggleCamera}
            disabled={!isCameraReady || !!error}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Camera className="w-5 h-5" />
            Switch
          </button>

          <button
            onClick={captureImage}
            disabled={!isCameraReady || !!error}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Camera className="w-5 h-5" />
            Capture
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebcamPalmReader;